// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { defaultPostOptions, defaultGetOptions, fetchGetWithRetryAsync, isAbortError, logErrorToConsole, defaultPutOptions } from '@/utils/apiUtils';
import { Type } from '@/utils/result';
import { ProfileInfo } from '@/models/models';
import { IConfirmProfileInfo, IContinueAsGuestDto, IPasswordStrengthDto, IProfileInfo, IResetPasswordDto, ISignInAuthenticatorDto, ISignInDto } from '@/interfaces';

const baseIdentityUrl = '';

export const userFailures = Object.freeze({
    EmailAlreadyExists: 'Email Already Exists',
    ServerError: 'Server Error',
    InvalidCreds: 'Invalid email or password',
    InvalidEmail: '',
    InvalidPassword: '',
});

/**
 * A cancelled request is not a failure, so the AbortError is handed back
 * as-is and callers can tell it apart with isAbortError.
 */
function toFailure(error: unknown): Error {
    if (isAbortError(error)) {
        return error as Error;
    }

    logErrorToConsole(error);
    return new Error(userFailures.ServerError);
}

export function getSignInDto(email: string, password: string): ISignInDto {
    return {
        email,
        password,
        returnUrl: '',
    };
}

export async function postSignInAsync(
    signInDto: ISignInDto,
    signal?: AbortSignal
): Promise<Type<any>> {
    try {
        const url = `${baseIdentityUrl}/api/signin`;
        const response = await fetch(url, defaultPostOptions(signInDto, signal));
        if (response.ok) {
            return await response.json();
        } else {
            return await getSigninError(response);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function postSignInAuthenticatorAsync(
    signInDto: ISignInAuthenticatorDto,
    signal?: AbortSignal
): Promise<Type<any>> {
    try {
        const url = `${baseIdentityUrl}/api/signin-authenticator`;
        const response = await fetch(url, defaultPostOptions(signInDto, signal));
        if (response.ok) {
            return await response.json();
        } else {
            return await getSigninError(response);
        }
    } catch (error) {
        return toFailure(error);
    }
}

async function getSigninError(response: Response): Promise<Error> {
    let message: string;
    if (response.status === 401) {
        message = userFailures.InvalidCreds;
    } else {
        message = userFailures.ServerError;
    }
    return new Error(message);
}

export async function postContinueAsGuestAsync(
    continueAsGuestDto: IContinueAsGuestDto,
    signal?: AbortSignal
): Promise<Type<string>> {
    try {
        const url = `${baseIdentityUrl}/api/continueAsGuest`;
        const response = await fetch(
            url,
            defaultPostOptions(continueAsGuestDto.returnUrl, signal)
        );
        if (response.ok) {
            return await response.text();
        } else {
            throw new Error(userFailures.ServerError);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function postSignUpAsync(
    profileInfo: IProfileInfo,
    signal?: AbortSignal
): Promise<Type<string | Error>> {
    try {
        const url = `${baseIdentityUrl}/api/v2/customer`;
        const response = await fetch(
            url,
            defaultPostOptions(profileInfo, signal)
        );
        if (response.ok) {
            return response.text();
        } else if (response.status === 409) {
            return new Error(userFailures.EmailAlreadyExists);
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function postForgotPasswordAsync(
    email: string,
    signal?: AbortSignal
): Promise<Type<boolean>> {
    try {
        const url = `${baseIdentityUrl}/api/email/forgot-password`;
        const response = await fetch(url, defaultPostOptions({ email }, signal));
        if (response.ok) {
            return true;
        } else {
            return new Error(userFailures.ServerError);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function postPasswordStrengthAsync(
    passwordStrength: IPasswordStrengthDto,
    signal?: AbortSignal
): Promise<Type<any>> {
    try {
        const url = `${baseIdentityUrl}/api/user/password/strength`;
        const response = await fetch(
            url,
            defaultPostOptions(passwordStrength, signal)
        );
        if (response.ok) {
            return await response.json();
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function putResetPasswordAsync(
    resetPasswordDto: IResetPasswordDto,
    signal?: AbortSignal
): Promise<Type<boolean>> {
    try {
        const url = `${baseIdentityUrl}/api/user/password/reset`;
        const response = await fetch(
            url,
            defaultPutOptions(resetPasswordDto, signal)
        );
        if (response.ok) {
            return true;
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function putConfirmProfileAsync(
    confirmProfileInfo: IConfirmProfileInfo,
    token: string,
    signal?: AbortSignal
): Promise<Type<boolean>> {
    try {
        const url = `${baseIdentityUrl}/api/profile/confirm`;
        const options = defaultPutOptions(confirmProfileInfo, signal);
        const headers = options.headers as Headers;
        headers.set('Authorization', `Bearer ${ token}`);
        const response = await fetch(url, options);
        if (response.ok) {
            return true;
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function getProfileAsync(
    token: string,
    signal?: AbortSignal
): Promise<Type<ProfileInfo>> {
    try {
        const url = `${baseIdentityUrl}/api/profile`;
        const fetchOptions = defaultGetOptions(signal);
        const headers = fetchOptions.headers as Headers;
        headers.set('Authorization', `Bearer ${ token}`);
        const response = await fetchGetWithRetryAsync(url, fetchOptions);
        if (response.ok) {
            const profile = await response.json();
            return ProfileInfo.fromProfileGetDto(profile);
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export default {
    postSignInAsync,
    postContinueAsGuestAsync,
    postSignUpAsync,
    postForgotPasswordAsync,
    postResetPasswordAsync: putResetPasswordAsync,
    getSignInDto,
    getProfileAsync,
    putConfirmProfileAsync,
};
